import React, { useEffect, useState } from 'react';
import { MapPinIcon, CalendarIcon, ClockIcon, MountainIcon, UsersIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
function useCountdown(targetDate: Date) {
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(targetDate));
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft(targetDate));
    }, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);
  return timeLeft;
}
function calculateTimeLeft(targetDate: Date) {
  const now = new Date().getTime();
  const target = targetDate.getTime();
  const difference = target - now;
  if (difference <= 0) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0
    };
  }
  return {
    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
    hours: Math.floor(difference / (1000 * 60 * 60) % 24),
    minutes: Math.floor(difference / (1000 * 60) % 60),
    seconds: Math.floor(difference / 1000 % 60)
  };
}
export function RaceInfo() {
  const {
    t
  } = useLanguage();
  const raceDate = new Date('2026-05-16T09:00:00');
  const {
    days,
    hours,
    minutes,
    seconds
  } = useCountdown(raceDate);
  return <section id="info" className="w-full bg-noise py-20 md:py-28 px-4 pt-[10px] pb-[10px]" data-id="element-258">
      <div className="max-w-7xl mx-auto" data-id="element-259">
        {/* Section Header */}
        <div className="text-center mb-16" data-id="element-260">
          <p className="text-orange-500 font-semibold text-sm uppercase tracking-[0.3em] mb-3" data-id="element-261">
            {t('raceInfo.markCalendar')}
          </p>
          <h2 className="font-display text-4xl md:text-6xl tracking-wider text-white mb-4" data-id="element-262">
            {t('raceInfo.title')}
          </h2>
        </div>

        {/* Countdown Timer */}
        <div className="mb-16" data-id="element-263">
          <p className="text-center text-gray-400 text-sm uppercase tracking-wider mb-6" data-id="element-264">
            {t('raceInfo.startsIn')}
          </p>
          <div className="flex items-center justify-center gap-3 md:gap-6" role="timer" aria-label="Countdown to race start" data-id="element-265">
            <div className="text-center" data-id="element-266">
              <div className="w-20 h-20 md:w-28 md:h-28 bg-neutral-900 border border-neutral-700 rounded-xl flex items-center justify-center glow-orange" data-id="element-267">
                <span className="font-display text-4xl md:text-6xl text-white" data-id="element-268">
                  {String(days).padStart(2, '0')}
                </span>
              </div>
              <p className="text-xs md:text-sm text-gray-500 uppercase tracking-wider mt-2" data-id="element-269">
                {t('raceInfo.days')}
              </p>
            </div>
            <span className="font-display text-3xl md:text-5xl text-orange-500 -mt-6" aria-hidden="true" data-id="element-270">
              :
            </span>
            <div className="text-center" data-id="element-271">
              <div className="w-20 h-20 md:w-28 md:h-28 bg-neutral-900 border border-neutral-700 rounded-xl flex items-center justify-center" data-id="element-272">
                <span className="font-display text-4xl md:text-6xl text-white" data-id="element-273">
                  {String(hours).padStart(2, '0')}
                </span>
              </div>
              <p className="text-xs md:text-sm text-gray-500 uppercase tracking-wider mt-2" data-id="element-274">
                {t('raceInfo.hours')}
              </p>
            </div>
            <span className="font-display text-3xl md:text-5xl text-orange-500 -mt-6" aria-hidden="true" data-id="element-275">
              :
            </span>
            <div className="text-center" data-id="element-276">
              <div className="w-20 h-20 md:w-28 md:h-28 bg-neutral-900 border border-neutral-700 rounded-xl flex items-center justify-center" data-id="element-277">
                <span className="font-display text-4xl md:text-6xl text-white" data-id="element-278">
                  {String(minutes).padStart(2, '0')}
                </span>
              </div>
              <p className="text-xs md:text-sm text-gray-500 uppercase tracking-wider mt-2" data-id="element-279">
                {t('raceInfo.minutes')}
              </p>
            </div>
            <span className="font-display text-3xl md:text-5xl text-orange-500 -mt-6" aria-hidden="true" data-id="element-280">
              :
            </span>
            <div className="text-center" data-id="element-281">
              <div className="w-20 h-20 md:w-28 md:h-28 bg-neutral-900 border border-neutral-700 rounded-xl flex items-center justify-center" data-id="element-282">
                <span className="font-display text-4xl md:text-6xl text-orange-500" data-id="element-283">
                  {String(seconds).padStart(2, '0')}
                </span>
              </div>
              <p className="text-xs md:text-sm text-gray-500 uppercase tracking-wider mt-2" data-id="element-284">
                {t('raceInfo.seconds')}
              </p>
            </div>
          </div>
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-285">
          {/* Race Format */}
          <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6 hover:border-orange-500/30 transition-colors" data-id="element-286">
            <div className="flex items-center gap-3 mb-4" data-id="element-287">
              <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center" data-id="element-288">
                <ClockIcon className="h-5 w-5 text-orange-500" data-id="element-289" />
              </div>
              <h3 className="font-bold text-white text-lg" data-id="element-290">
                {t('raceInfo.raceFormat')}
              </h3>
            </div>
            <p className="text-gray-400 text-sm mb-3" data-id="element-291">
              {t('raceInfo.formatDesc')}
            </p>
          </div>

          {/* Friday Schedule - SuperEnduro */}
          <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6 hover:border-orange-500/30 transition-colors" data-id="element-292">
            <div className="flex items-center gap-3 mb-4" data-id="element-293">
              <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center" data-id="element-294">
                <CalendarIcon className="h-5 w-5 text-orange-500" data-id="element-295" />
              </div>
              <h3 className="font-bold text-white text-lg" data-id="element-296">
                {t('raceInfo.superEnduro')}
              </h3>
            </div>
            <p className="text-gray-300 font-semibold mb-3" data-id="element-297">
              {t('raceInfo.friday')}
            </p>
            <div className="space-y-2 text-sm" data-id="element-298">
              <div className="flex justify-between" data-id="element-299">
                <span className="text-gray-500" data-id="element-300">
                  {t('raceInfo.registration')}
                </span>
                <span className="text-gray-300 font-medium" data-id="element-301">15:00</span>
              </div>
              <div className="flex justify-between" data-id="element-302">
                <span className="text-gray-500" data-id="element-303">
                  {t('raceInfo.ridersBriefing')}
                </span>
                <span className="text-gray-300 font-medium" data-id="element-304">15:50</span>
              </div>
              <div className="flex justify-between" data-id="element-305">
                <span className="text-gray-500" data-id="element-306">
                  {t('raceInfo.freePractice')}
                </span>
                <span className="text-gray-300 font-medium" data-id="element-307">16:00–16:20</span>
              </div>
              <div className="flex justify-between" data-id="element-308">
                <span className="text-gray-500" data-id="element-309">
                  {t('raceInfo.qualification')}
                </span>
                <span className="text-gray-300 font-medium" data-id="element-310">16:30–17:30</span>
              </div>
              <div className="flex justify-between" data-id="element-311">
                <span className="text-gray-500" data-id="element-312">
                  {t('raceInfo.lastChance')}
                </span>
                <span className="text-gray-300 font-medium" data-id="element-313">17:45–18:00</span>
              </div>
              <div className="flex justify-between" data-id="element-314">
                <span className="text-gray-500" data-id="element-315">
                  {t('raceInfo.semiFinals')}
                </span>
                <span className="text-gray-300 font-medium" data-id="element-316">18:00–18:30</span>
              </div>
              <div className="flex justify-between" data-id="element-317">
                <span className="text-orange-500 font-semibold" data-id="element-318">
                  {t('raceInfo.superFinal')}
                </span>
                <span className="text-orange-500 font-bold" data-id="element-319">19:00</span>
              </div>
            </div>
          </div>

          {/* Date & Schedule */}
          <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6 hover:border-orange-500/30 transition-colors" data-id="element-320">
            <div className="flex items-center gap-3 mb-4" data-id="element-321">
              <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center" data-id="element-322">
                <CalendarIcon className="h-5 w-5 text-orange-500" data-id="element-323" />
              </div>
              <h3 className="font-bold text-white text-lg" data-id="element-324">
                {t('raceInfo.hardEnduroRace')}
              </h3>
            </div>
            <p className="text-gray-300 font-semibold mb-3" data-id="element-325">
              {t('raceInfo.saturday')}
            </p>
            <div className="space-y-2 text-sm" data-id="element-326">
              <div className="flex justify-between" data-id="element-327">
                <span className="text-gray-500" data-id="element-328">
                  {t('raceInfo.registration')}{' '}
                </span>
                <span className="text-gray-300 font-medium" data-id="element-329">08:00</span>
              </div>
              <div className="flex justify-between" data-id="element-330">
                <span className="text-gray-500" data-id="element-331">
                  {t('raceInfo.technicalInspection')}{' '}
                </span>
                <span className="text-gray-300 font-medium" data-id="element-332">8:45-11:00</span>
              </div>
              <div className="flex justify-between" data-id="element-333">
                <span className="text-gray-500" data-id="element-334">
                  {t('raceInfo.ridersBriefing')}
                </span>
                <span className="text-gray-300 font-medium" data-id="element-335">11:15</span>
              </div>
              <div className="flex justify-between" data-id="element-336">
                <span className="text-orange-500 font-semibold" data-id="element-337">
                  {t('raceInfo.raceStart')}
                </span>
                <span className="text-orange-500 font-bold" data-id="element-338">12:00</span>
              </div>
              <div className="flex justify-between" data-id="element-339">
                <span className="text-gray-500" data-id="element-340">
                  {t('raceInfo.awardCeremonyFull')}
                </span>
                <span className="text-gray-300 font-medium" data-id="element-341">17:30</span>
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6 hover:border-orange-500/30 transition-colors" data-id="element-342">
            <div className="flex items-center gap-3 mb-4" data-id="element-343">
              <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center" data-id="element-344">
                <MapPinIcon className="h-5 w-5 text-orange-500" data-id="element-345" />
              </div>
              <h3 className="font-bold text-white text-lg" data-id="element-346">
                {t('raceInfo.location')}
              </h3>
            </div>
            <p className="text-gray-300 font-semibold mb-1" data-id="element-347">
              Pāvuli, Raunas pagasts
            </p>
            <p className="text-gray-500 text-sm mb-3" data-id="element-348">
              Smiltenes novads, Latvija, LV-4131
            </p>
            <p className="text-gray-400 text-sm" data-id="element-349">
              {t('raceInfo.locationDesc')}
            </p>
          </div>

          {/* Terrain */}
          <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6 hover:border-orange-500/30 transition-colors" data-id="element-350">
            <div className="flex items-center gap-3 mb-4" data-id="element-351">
              <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center" data-id="element-352">
                <MountainIcon className="h-5 w-5 text-orange-500" data-id="element-353" />
              </div>
              <h3 className="font-bold text-white text-lg" data-id="element-354">
                {t('raceInfo.terrain')}
              </h3>
            </div>
            <p className="text-gray-400 text-sm mb-3" data-id="element-355">
              {t('raceInfo.terrainDesc')}
            </p>
            <div className="flex flex-wrap gap-2" data-id="element-356">
              <span className="px-3 py-1 bg-neutral-800 text-gray-300 text-xs rounded-full" data-id="element-357">
                {t('raceInfo.sandDunes')}
              </span>
              <span className="px-3 py-1 bg-neutral-800 text-gray-300 text-xs rounded-full" data-id="element-358">
                {t('raceInfo.rockGarden')}
              </span>
              <span className="px-3 py-1 bg-neutral-800 text-gray-300 text-xs rounded-full" data-id="element-359">
                {t('raceInfo.forestTrails')}
              </span>
              <span className="px-3 py-1 bg-neutral-800 text-gray-300 text-xs rounded-full" data-id="element-360">
                {t('raceInfo.drops')}
              </span>
              <span className="px-3 py-1 bg-neutral-800 text-gray-300 text-xs rounded-full" data-id="element-361">
                {t('raceInfo.mudPits')}
              </span>
              <span className="px-3 py-1 bg-neutral-800 text-gray-300 text-xs rounded-full" data-id="element-362">
                {t('raceInfo.hillClimbs')}
              </span>
              <span className="px-3 py-1 bg-neutral-800 text-gray-300 text-xs rounded-full" data-id="element-363">
                {t('raceInfo.logObstacles')}
              </span>
              <span className="px-3 py-1 bg-neutral-800 text-gray-300 text-xs rounded-full" data-id="element-364">
                {t('raceInfo.gravelRoads')}
              </span>
            </div>
          </div>

          {/* Participants */}
          <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6 hover:border-orange-500/30 transition-colors" data-id="element-365">
            <div className="flex items-center gap-3 mb-4" data-id="element-366">
              <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center" data-id="element-367">
                <UsersIcon className="h-5 w-5 text-orange-500" data-id="element-368" />
              </div>
              <h3 className="font-bold text-white text-lg" data-id="element-369">
                {t('raceInfo.participants')}
              </h3>
            </div>
            <p className="text-gray-400 text-sm mb-3" data-id="element-370">
              {t('raceInfo.earlyRegistered')}
            </p>
            <div className="space-y-2 text-sm" data-id="element-371">
              <div className="flex justify-between" data-id="element-372">
                <span className="text-green-500" data-id="element-373">
                  {t('raceInfo.beginners')}
                </span>
                <span className="text-gray-300" data-id="element-374">33</span>
              </div>
              <div className="flex justify-between" data-id="element-375">
                <span className="text-orange-500" data-id="element-376">{t('raceInfo.hobby')}</span>
                <span className="text-gray-300" data-id="element-377">50</span>
              </div>
              <div className="flex justify-between" data-id="element-378">
                <span className="text-red-500" data-id="element-379">{t('raceInfo.pro')}</span>
                <span className="text-gray-300" data-id="element-380">22</span>
              </div>
              <div className="flex justify-between border-t border-neutral-700 pt-2 mt-2" data-id="element-381">
                <span className="text-white font-semibold" data-id="element-382">
                  {t('raceInfo.total')}
                </span>
                <span className="text-white font-semibold" data-id="element-383">105 braucēji</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}